'use client';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type CSSProperties,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type {
  MenuData,
  Template,
  TemplateThemeMode,
} from '@/app/dashboard/templates/components/types/template';
import {
  normalizeFontFamily,
} from '@/app/dashboard/templates/components/lib/util';
import {
  useResponsiveScale,
} from '@/app/dashboard/templates/components/lib/templateResponsive';
import { getTemplatePagedNodes } from '@/app/dashboard/templates/components/lib/pagedFlipTemplate';
import { classify } from '@/lib/mnrq/paged-flip/gesture';
import {
  buildPagedFlipDocument,
  buildPagedFlipMeasuredCategoryKey,
  buildPagedFlipMeasuredItemKey,
  buildPagedFlipMeasurementBucketKey,
  ensureEvenPagedFlipPages,
  resolvePagedFlipConfig,
} from '@/lib/mnrq/paged-flip/engine';
import type { PagedFlipMeasurementSnapshot, PagedFlipPage } from '@/lib/mnrq/paged-flip/types';
import { renderFlipPageTemplate } from './PagedFlipNodeRenderer';

import {
  resolveTemplateVariables,      // replaces the old util.ts import of same name
  resolveCanonicalVariables,     // NEW , use in paged-flip palette builder
  buildTemplateCssVars,          // NEW , CSS var injection
  buildTemplateThemeContextValue,// NEW , context value builder
  resolveTemplateTokenString,
  ResolvedTemplateVariables,    // replaces the old util.ts import of same name
} from "../components/lib/templateVariables";
import { TemplateThemeContext } from "../components/lib/templateThemeContext";



const HTMLFlipBook = dynamic(() => import('react-pageflip'), { ssr: false }) as any;

const SWIPE_HINT_STORAGE_KEY = 'mnrq:flipbook-swipe-hint-seen';

const TEMPLATE_THEME_SURFACE_VARS: Record<TemplateThemeMode, CSSProperties> = {
  light: {
    '--background': '#ffffff',
    '--foreground': '#0f0f0f',
    '--card': '#ffffff',
    '--card-foreground': '#0f0f0f',
    '--accent': '#ff5a3c',
    '--border': 'rgba(15, 15, 15, 0.12)',
  } as CSSProperties,
  dark: {
    '--background': '#0f0f0f',
    '--foreground': '#f5f5f5',
    '--card': '#171717',
    '--card-foreground': '#f5f5f5',
    '--accent': '#ff5a3c',
    '--border': 'rgba(245, 245, 245, 0.12)',
  } as CSSProperties,
};

const BOOK_WIDTH = 560;
const MOBILE_BOOK_HEIGHT_RATIO = 0.96;
/**
 * Desktop's share of the viewport height, mirroring the mobile ratio above.
 *
 * Desktop used to take `viewportHeight − 120 (top chrome) − 80 (bottom chrome)` and
 * nothing else, so on an ordinary ~900px viewport the book rendered at 700px while
 * mobile filled 96% of its screen. The flat 200px allowance is a worst-case guess at
 * the chrome, and on desktop — where there is the most room to spare — it was the
 * thing making the book look small.
 *
 * Taking the LARGER of the two calculations keeps the subtractive path as a floor
 * (so a genuinely short window still fits) while letting a normal window use the
 * ratio. 0.90 leaves ~10% for the header and controls; mobile lives on 4%.
 */
const DESKTOP_BOOK_HEIGHT_RATIO = 0.9;

type FlipBookApi = {
  pageFlip: () => {
    flip: (pageNum: number, corner?: 'top' | 'bottom') => void;
    flipNext: (corner?: 'top' | 'bottom') => void;
    flipPrev: (corner?: 'top' | 'bottom') => void;
    turnToPage: (pageNum: number) => void;
  };
};

function pageHasItems(page: PagedFlipPage) {
  return (page.categories || []).some((category) => (category.items || []).length > 0);
}

function pageText(value: string | undefined) {
  return typeof value === 'string' ? value.trim() : '';
}

type FlipEvent = { data?: number };
type OrientationEvent = { data?: 'portrait' | 'landscape' };

type PagePalette = {
  canvasBackground: string;
  pageBackground: string;
  pageAccent: string;
  pageAccentSoft: string;
  border: string;
  heading: string;
  eyebrow: string;
  sectionTitle: string;
  itemTitle: string;
  itemDesc: string;
  price: string;
  divider: string;
  promoBg: string;
  promoText: string;
  promoBorder: string;
  pageShadow: string;
};

type Typography = {
  heading: string;
  subheading: string;
  section: string;
  item: string;
  body: string;
  price: string;
};

type RepeatedRootGroup = {
  id: string;
  repeatIndex: number;
  continuation: boolean;
  elements: HTMLElement[];
};

type RepeatedItemGroup = {
  itemId: string;
  repeatIndex: number;
  elements: HTMLElement[];
};

const EMPTY_MEASUREMENT_SNAPSHOT: PagedFlipMeasurementSnapshot = {
  itemWeights: {},
  categoryHeaderWeights: {},
  categoryFooterWeights: {},
  pageBaseWeights: {},
  blockGapWeights: {},
};

// Hard cap on measure -> correct -> remeasure cycles. Convergence is normally reached
// in 1-2 passes; this only fires for a pathological template where measured overflow
// keeps disagreeing pass over pass, so we force-settle on the current layout rather
// than looping (and re-rendering) indefinitely.
const MAX_MEASUREMENT_PASSES = 8;

// Maximum number of automatic retry attempts when the HTMLFlipBook library fails to
// initialise. Each retry resets the book instance so react-pageflip re-mounts from
// scratch , transient race conditions (e.g. late font loading, zero-size container on
// first paint) usually succeed on the second attempt.
const MAX_FLIP_RETRIES = 3;
const MAX_MEASURED_CATEGORY_FOOTER_WEIGHT = 48;

function roundMeasuredPixels(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value));
}

function getGroupBounds(elements: HTMLElement[]) {
  if (elements.length === 0) {
    return { top: 0, bottom: 0 };
  }

  return elements.reduce(
    (bounds, element) => {
      const rect = element.getBoundingClientRect();
      return {
        top: Math.min(bounds.top, rect.top),
        bottom: Math.max(bounds.bottom, rect.bottom),
      };
    },
    {
      top: Number.POSITIVE_INFINITY,
      bottom: Number.NEGATIVE_INFINITY,
    },
  );
}

function groupCategoryRoots(root: ParentNode) {
  const groups = new Map<string, RepeatedRootGroup>();
  const elements = Array.from(
    root.querySelectorAll<HTMLElement>('[data-repeat-root="true"][data-repeat-context="category"][data-category-id]'),
  );

  elements.forEach((element) => {
    const categoryId = String(element.dataset.categoryId || '').trim();
    if (!categoryId) return;

    const repeatIndex = Number(element.dataset.repeatIndex || 0);
    const continuation = element.dataset.categoryContinuation === 'true';
    const key = `${repeatIndex}:${categoryId}:${continuation ? '1' : '0'}`;
    const existing = groups.get(key);

    if (existing) {
      existing.elements.push(element);
      return;
    }

    groups.set(key, {
      id: categoryId,
      repeatIndex,
      continuation,
      elements: [element],
    });
  });

  return Array.from(groups.values()).sort((left, right) => left.repeatIndex - right.repeatIndex);
}

function groupItemRoots(elements: HTMLElement[]) {
  const groups = new Map<string, RepeatedItemGroup>();

  elements.forEach((container) => {
    const itemElements = Array.from(
      container.querySelectorAll<HTMLElement>('[data-repeat-root="true"][data-repeat-context="item"][data-item-id]'),
    );

    itemElements.forEach((itemElement) => {
      const itemId = String(itemElement.dataset.itemId || '').trim();
      if (!itemId) return;

      const repeatIndex = Number(itemElement.dataset.repeatIndex || 0);
      const key = `${repeatIndex}:${itemId}`;
      const existing = groups.get(key);

      if (existing) {
        existing.elements.push(itemElement);
        return;
      }

      groups.set(key, {
        itemId,
        repeatIndex,
        elements: [itemElement],
      });
    });
  });

  return Array.from(groups.values()).sort((left, right) => left.repeatIndex - right.repeatIndex);
}

function collectCategoryMeasurements(group: RepeatedRootGroup) {
  const categoryBounds = getGroupBounds(group.elements);
  const itemGroups = groupItemRoots(group.elements);

  if (itemGroups.length === 0) {
    return {
      headerHeight: roundMeasuredPixels(categoryBounds.bottom - categoryBounds.top),
      footerHeight: 0,
      itemWeights: new Map<string, number>(),
      rows: [] as Array<{ firstItemId: string; repeatIndex: number; top: number; bottom: number }>,
      top: categoryBounds.top,
      bottom: categoryBounds.bottom,
    };
  }

  const itemRects = itemGroups.map((itemGroup) => {
    const bounds = getGroupBounds(itemGroup.elements);
    return {
      itemId: itemGroup.itemId,
      repeatIndex: itemGroup.repeatIndex,
      top: bounds.top - categoryBounds.top,
      bottom: bounds.bottom - categoryBounds.top,
    };
  });

  const rows: Array<{
    top: number;
    bottom: number;
    items: typeof itemRects;
  }> = [];

  itemRects.forEach((entry) => {
    const activeRow = rows[rows.length - 1];
    if (activeRow && Math.abs(activeRow.top - entry.top) <= 2) {
      activeRow.items.push(entry);
      activeRow.bottom = Math.max(activeRow.bottom, entry.bottom);
      activeRow.items.sort((left, right) => left.repeatIndex - right.repeatIndex);
      return;
    }

    rows.push({
      top: entry.top,
      bottom: entry.bottom,
      items: [entry],
    });
  });

  const itemWeights = new Map<string, number>();
  const rowsForOverflow = rows.map((row) => ({
    firstItemId: row.items[0]?.itemId || '',
    repeatIndex: row.items[0]?.repeatIndex || 0,
    top: row.top + categoryBounds.top,
    bottom: row.bottom + categoryBounds.top,
  })).filter((row) => row.firstItemId);

  rows.forEach((row, rowIndex) => {
    const nextRow = rows[rowIndex + 1];
    const rowWeight = roundMeasuredPixels(nextRow ? nextRow.top - row.top : row.bottom - row.top);
    row.items.forEach((entry, itemIndex) => {
      itemWeights.set(entry.itemId, itemIndex === 0 ? rowWeight : 0);
    });
  });

  return {
    headerHeight: roundMeasuredPixels(rows[0]?.top ?? 0),
    footerHeight: Math.min(
      MAX_MEASURED_CATEGORY_FOOTER_WEIGHT,
      roundMeasuredPixels(Math.max(0, categoryBounds.bottom - (rows[rows.length - 1]?.bottom ?? categoryBounds.bottom))),
    ),
    itemWeights,
    rows: rowsForOverflow,
    top: categoryBounds.top,
    bottom: categoryBounds.bottom,
  };
}

function mergeMeasuredMap(
  current: Record<string, number>,
  incoming: Record<string, number>,
) {
  let changed = false;
  const next = { ...current };

  Object.entries(incoming).forEach(([key, value]) => {
    if (!Number.isFinite(value)) return;
    if (next[key] === value) return;
    next[key] = value;
    changed = true;
  });

  return { changed, next };
}

function useMobileBreakpoint() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const sync = () => setIsMobile(window.innerWidth < 768);
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  return isMobile;
}

function useViewportSize() {
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useEffect(() => {
    // The book's height and its remount key both derive from this. On a phone,
    // scrolling back to the top re-expands the browser's address bar (and opening
    // the keyboard shrinks the window): a height-only change of ~50-300px with the
    // width untouched. Following it remounted the flipbook at page 1 and hid it
    // while it re-measured, on every scroll to the top. Only a width change (a
    // rotation) or a large height change is a real new viewport.
    const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    const sync = () => {
      const next = { width: window.innerWidth, height: window.innerHeight };
      setViewport((prev) => {
        if (prev.width === next.width && prev.height === next.height) return prev;
        if (
          coarsePointer &&
          prev.width > 0 &&
          prev.width === next.width &&
          Math.abs(prev.height - next.height) < 320
        ) {
          return prev;
        }
        return next;
      });
    };

    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  return viewport;
}

function getTypography(templateResolvedVariables: ReturnType<typeof resolveTemplateVariables>): Typography {
  const fonts = templateResolvedVariables.fonts || {};
  // normalizeFontFamily maps font names to their next/font CSS variables;
  // bare names never match the loaded families directly.
  return {
    heading: normalizeFontFamily(String(fonts.heading || fonts.header || "'Playfair Display', serif")),
    subheading: normalizeFontFamily(String(fonts.subheading || fonts.body || "'Inter', sans-serif")),
    section: normalizeFontFamily(String(fonts.section || fonts.heading || "'Bebas Neue', sans-serif")),
    item: normalizeFontFamily(String(fonts.item || fonts.body || "'Inter', sans-serif")),
    body: normalizeFontFamily(String(fonts.body || fonts.subheading || "'Inter', sans-serif")),
    price: normalizeFontFamily(String(fonts.price || fonts.body || "'Inter', sans-serif")),
  };
}

  // NEW (use canonical keys directly , defaults are guaranteed):
function getPalette(
  template: Template,
  variables: ResolvedTemplateVariables,
): PagePalette {
  const c = variables.colors;
  // Theme gradient layer wins over the flat canvas value; the ambient canvas
  // style already detects `gradient(` strings and renders them as an image
  // layer with a readability veil (resolveAmbientCanvasStyle).
  const canvasBackground =
    c.backgroundGradient ||
    resolveTemplateTokenString(
      template.canvas?.background?.value || c.background || '#f4efe8',
      variables,
    ) || c.background || '#f4efe8';

  return {
    canvasBackground,
    pageBackground:   c.panel          || '#fffdf9',
    pageAccent:       c.accent         || '#9d5137',
    pageAccentSoft:   c.secondary      || 'rgba(157,81,55,0.08)',
    border:           c.panelBorder    || 'rgba(15,15,15,0.12)',
    heading:          c.heading        || '#111827',
    eyebrow:          c.subheading     || '#526172',
    sectionTitle:     c.sectionTitle   || '#1f2937',
    itemTitle:        c.itemTitle      || '#111827',
    itemDesc:         c.itemDesc       || '#526172',
    price:            c.price          || '#111827',
    divider:          c.divider        || 'rgba(15,15,15,0.12)',
    promoBg:          c.promoBg        || 'rgba(255,90,60,0.12)',
    promoText:        c.promoText      || '#9a3412',
    promoBorder:      c.promoBorder    || 'rgba(15,15,15,0.12)',
    pageShadow:       '0 28px 80px rgba(15,23,32,0.10)',
  };
}
 

function resolvePagedFlipThemeMode(
  template: Template,
  forcedThemeMode?: TemplateThemeMode,
): TemplateThemeMode {
  if (forcedThemeMode) return forcedThemeMode;
  if (template.themeBehavior === 'fixed-dark') return 'dark';
  if (template.themeBehavior === 'fixed-light') return 'light';
  if (template.themeVariants?.dark && !template.themeVariants?.light) return 'dark';
  return 'light';
}

function resolveAmbientCanvasStyle(
  canvasBackground: string,
  pageAccent: string,
  mode: TemplateThemeMode,
): CSSProperties {
  const rawBackground = canvasBackground.trim() || (mode === 'dark' ? '#0f0f0f' : '#f4efe8');
  const isDecorativeBackground = /gradient\(|url\(|image-set\(|var\(/.test(rawBackground);
  const veil = mode === 'dark'
    ? 'linear-gradient(180deg, rgba(7,10,14,0.72), rgba(7,10,14,0.84))'
    : 'linear-gradient(180deg, rgba(255,255,255,0.70), rgba(255,255,255,0.82))';
  const accentMist = `radial-gradient(circle at 18% 18%, color-mix(in srgb, ${pageAccent} 16%, transparent), transparent 54%)`;

  return {
    backgroundColor: isDecorativeBackground ? undefined : rawBackground,
    backgroundImage: isDecorativeBackground ? `${veil}, ${accentMist}, ${rawBackground}` : `${veil}, ${accentMist}`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: isDecorativeBackground ? 'cover, auto, cover' : 'cover, auto',
  };
}

function BookBindingOverlay({
  isMobile,
  orientation,
  palette,
  mode,
}: {
  isMobile: boolean;
  orientation: 'portrait' | 'landscape';
  palette: PagePalette;
  mode: TemplateThemeMode;
}) {
  const landscape = !isMobile && orientation === 'landscape';

  if (landscape) {
    return (
      <div className="pointer-events-none absolute inset-y-5 left-1/2 z-[15] hidden w-[80px] -translate-x-1/2 sm:block">
        <div
          className="absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 rounded-full"
          style={{
            background: `linear-gradient(180deg, transparent, ${palette.border}, transparent)`,
          }}
        />
        <div
          className="absolute inset-y-3 inset-x-0 rounded-full blur-[14px]"
          style={{
            background:
              mode === 'dark'
                ? 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.28) 28%, rgba(255,255,255,0.06) 50%, rgba(0,0,0,0.28) 72%, transparent 100%)'
                : 'linear-gradient(90deg, transparent 0%, rgba(15,23,32,0.10) 28%, rgba(255,255,255,0.84) 50%, rgba(15,23,32,0.10) 72%, transparent 100%)',
          }}
        />
        <div
          className="absolute inset-y-10 left-1/2 w-[14px] -translate-x-1/2 rounded-full blur-md"
          style={{
            background: `linear-gradient(180deg, transparent, ${palette.pageAccentSoft}, transparent)`,
          }}
        />
      </div>
    );
  }

  return (
    <div className="pointer-events-none absolute inset-y-5 left-0 z-[15] w-10">
      <div
        className="absolute inset-y-0 left-0 w-[2px] rounded-full"
        style={{
          background: `linear-gradient(180deg, transparent, ${palette.border}, transparent)`,
        }}
      />
      <div
        className="absolute inset-y-4 left-0 w-8 rounded-r-full blur-[10px]"
        style={{
          background:
            mode === 'dark'
              ? 'linear-gradient(90deg, rgba(0,0,0,0.30), rgba(255,255,255,0.04), transparent)'
              : 'linear-gradient(90deg, rgba(15,23,32,0.10), rgba(255,255,255,0.76), transparent)',
        }}
      />
    </div>
  );
}

function getSpreadStartIndex(pageIndex: number, hasCoverPage: boolean) {
  // Expected spread mapping:
  // cover enabled: 0 -> [cover], 1 -> [1,2], 2 -> [1,2], 3 -> [3,4]
  // no cover:      0 -> [0,1], 1 -> [0,1], 2 -> [2,3], 3 -> [2,3]
  if (pageIndex <= 0) return 0;
  if (!hasCoverPage) return pageIndex % 2 === 0 ? pageIndex : pageIndex - 1;
  return pageIndex % 2 === 0 ? Math.max(1, pageIndex - 1) : pageIndex;
}

function getStandalonePageOffset(
  currentPage: PagedFlipPage | undefined,
  pairedPage: PagedFlipPage | undefined,
  orientation: 'portrait' | 'landscape',
  isMobile: boolean,
  showCoverPage: boolean,
  visiblePageIndex: number,
  visiblePagesLength: number,
  pageWidth: number,
) {
  if (isMobile) return '0px';

  const spreadPages = [currentPage, pairedPage].filter(Boolean) as PagedFlipPage[];
  const hasCover = spreadPages.some((page) => page.kind === 'cover');
  const hasBackCover = spreadPages.some((page) => page.kind === 'back-cover');

  const coverOffset = hasCover ? `${-Math.round(pageWidth / 2)}px` : hasBackCover ? `${Math.round(pageWidth / 2)}px` : null;
  const legacyEdgeOffset =
    (orientation === 'landscape' || orientation === 'portrait') && showCoverPage && visiblePageIndex === 0
      ? '-25%'
      : (orientation === 'landscape' || orientation === 'portrait') && showCoverPage && visiblePageIndex >= visiblePagesLength - 1 && visiblePagesLength % 2 === 0
        ? '25%'
        : '0';

  return coverOffset || legacyEdgeOffset;
}

function waitForImages(root: ParentNode) {
  const images = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
  const pending = images.filter((image) => !image.complete);

  if (pending.length === 0) {
    return Promise.resolve();
  }

  return Promise.allSettled(
    pending.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (typeof image.decode === 'function') {
            image.decode().then(() => resolve()).catch(() => resolve());
            return;
          }

          image.addEventListener('load', () => resolve(), { once: true });
          image.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
}

function renderPageBody(
  template: Template,
  data: MenuData,
  document: ReturnType<typeof buildPagedFlipDocument>,
  page: PagedFlipPage,
  palette: PagePalette,
  typography: Typography,
  variables: ReturnType<typeof resolveTemplateVariables>,
  config: ReturnType<typeof resolvePagedFlipConfig>,
  isMobile: boolean,
  onJumpToChapter: (chapterId: string) => void,
  mode: TemplateThemeMode,
  scales: { font: number; spacing: number; layout: number },
  scaleTexts: boolean,
  isCompactSplitLayout: boolean,
) {
  const customSlotNodes = renderFlipPageTemplate({
    template,
    templateResolvedVariables: variables,
    data,
    document,
    page,
    onJumpToChapter,
    config,
    isMobile,
    mode,
    palette,
    typography,
    scales,
    scaleTexts,
    isCompactSplitLayout,
  });

  if (customSlotNodes) {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-2xl border">
        {customSlotNodes}
      </div>
    );
  }

  return null;
}

const FlipBookSheet = forwardRef<
  HTMLDivElement,
  {
    template: Template;
    data: MenuData;
    document: ReturnType<typeof buildPagedFlipDocument>;
    page: PagedFlipPage;
    pageIndex: number;
    palette: PagePalette;
    typography: Typography;
    mode: TemplateThemeMode;
    variables: ReturnType<typeof resolveTemplateVariables>;
    config: ReturnType<typeof resolvePagedFlipConfig>;
    isMobile: boolean;
    pageWidth: number;
    pageHeight: number;
    canvasBackground: string;
    onJumpToChapter: (chapterId: string) => void;
  }
>(function FlipBookSheet(
  {
    template,
    data,
    document,
    page,
    pageIndex,
    palette,
    typography,
    mode,
    variables,
    config,
    isMobile,
    pageWidth,
    pageHeight,
    canvasBackground,
    onJumpToChapter,
  },
  ref,
) {
  const { ref: responsiveRef, scales, isCompactSplitLayout } = useResponsiveScale(template);
  const scaleTexts = template.scaleTexts !== false;

    const templateResolvedVariables = useMemo(
      () => resolveTemplateVariables(template, mode),
      [template, mode]
    );

  const themeContextValue = useMemo(
      () => buildTemplateThemeContextValue(templateResolvedVariables, mode),
    [templateResolvedVariables, mode]
  );

  return (
    <div
      ref={ref}
      data-render-role="flipbook-sheet"
      data-page-index={pageIndex}
      data-page-id={page.id}
      data-page-kind={page.kind}
      data-page-layout={page.layout || config.contentLayout || 'stacked'}
      className="h-full w-full overflow-hidden"
      style={{ width: pageWidth, height: pageHeight }}
    >
        <TemplateThemeContext.Provider value={themeContextValue}>
          <div
            ref={responsiveRef}
            className="h-full w-full overflow-hidden"
            style={{
              background: canvasBackground,
              colorScheme: mode,
              ...TEMPLATE_THEME_SURFACE_VARS[mode],
              ...buildTemplateCssVars(templateResolvedVariables), // ← ADD
            }}
          >
            <div
              data-render-role="flipbook-page-scroll"
              className="h-full w-full overflow-hidden px-3 py-3 sm:px-7 sm:py-8"
            >
              {renderPageBody(
                template,
                data,
                document,
                page,
                palette,
                typography,
                variables,
                config,
                isMobile,
                onJumpToChapter,
                mode,
                scales,
                scaleTexts,
                isCompactSplitLayout,
              )}
            </div>
          </div>
        </TemplateThemeContext.Provider>
    </div>
  );
});

export function PagedFlipRenderer({
  template,
  data,
  showThemeToggle: _showThemeToggle = false,
  themeTogglePosition: _themeTogglePosition = 'top-right',
  forcedThemeMode,
  onThemeModeChange: _onThemeModeChange,
  viewportOffsetPx = 0,
  guidePreview = false,
  bottomChromeOffsetPx = 0,
}: {
  template: Template;
  data: MenuData;
  showThemeToggle?: boolean;
  themeTogglePosition?: 'bottom-right' | 'top-right';
  forcedThemeMode?: TemplateThemeMode;
  onThemeModeChange?: (mode: TemplateThemeMode) => void;
  viewportOffsetPx?: number;
  guidePreview?: boolean;
  /**
   * Extra pixels of fixed chrome below the book (e.g. a host shell's bottom
   * rail). Lifts the swipe hint and page indicator so they clear it on desktop
   * (`sm:bottom-*`); mobile already reserves 144px via `max-sm:bottom-36`.
   * The dashboard/builder embeds pass 0 and keep the full-viewport position.
   */
  bottomChromeOffsetPx?: number;
}) {
  const isMobile = useMobileBreakpoint();
  const viewport = useViewportSize();
  const [pageIndex, setPageIndex] = useState(0);
  const [bookUnavailable, setBookUnavailable] = useState(false);
  const [flipRetryCount, setFlipRetryCount] = useState(0);
  const flipRetryTimerRef = useRef<number | null>(null);
  const [bookFrameOpacity, setBookFrameOpacity] = useState(1);
  const bookFrameOpacityTimerRef = useRef<number | null>(null);
  const isFirstBookRenderRef = useRef(true);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [measurementSnapshot, setMeasurementSnapshot] = useState<PagedFlipMeasurementSnapshot>(EMPTY_MEASUREMENT_SNAPSHOT);
  const [isLayoutSettled, setIsLayoutSettled] = useState(false);
  const [, startPageIndexTransition] = useTransition();
  const bookRef = useRef<FlipBookApi | null>(null);
  const bookShellRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<HTMLDivElement | null>(null);
  const measurementRef = useRef<HTMLDivElement | null>(null);
  const pageIndexFrameRef = useRef<number | null>(null);
  const measurementPassCountRef = useRef(0);
  const hasSettledOnceRef = useRef(false);

  const activeThemeMode = useMemo(
    () => resolvePagedFlipThemeMode(template, forcedThemeMode),
    [template, forcedThemeMode],
  );
  const templateResolvedVariables = useMemo(
    () => resolveTemplateVariables(template, activeThemeMode),
    [template, activeThemeMode],
  );
  const config = useMemo(() => resolvePagedFlipConfig(template), [template]);
  const rawViewportWidth = viewport.width || 360;
  const rawViewportHeight = (viewport.height || 640) - viewportOffsetPx;
  const safeViewportHeight = Math.max(rawViewportHeight, 320);

  // Safe structural assumption for pre-headless builder constraint allocation
  const topChromeHeight = isMobile ? 56 : 120;
  const bottomChromeHeight = isMobile ? 80 : 80 + bottomChromeOffsetPx;
  
  const availableHeight = Math.max(320, safeViewportHeight - topChromeHeight - bottomChromeHeight);

  // Maximum caps for clean, clear structural boundaries
  const MAX_BOOK_HEIGHT = 1060;

  // Calculate safe device pixel height independently for exact headless scaling propagation
  const targetDeviceHeight = useMemo(() => {
    const mobileHeight = Math.round(safeViewportHeight * MOBILE_BOOK_HEIGHT_RATIO);
    // Desktop takes whichever is taller: the chrome-subtracted floor, or its share of
    // the viewport. `availableHeight` alone left ~200px unused on every normal window.
    const desktopHeight = Math.max(
      availableHeight,
      Math.round(safeViewportHeight * DESKTOP_BOOK_HEIGHT_RATIO),
    );
    const rawHeight = isMobile ? mobileHeight : desktopHeight;
    return Math.min(Math.max(rawHeight, 400), MAX_BOOK_HEIGHT);
  }, [isMobile, safeViewportHeight, availableHeight]);
  const measurementViewport = isMobile ? 'mobile' : 'desktop';
  const measurementScopeKey = useMemo(
    () =>
      [
        template.meta.slug,
        activeThemeMode,
        measurementViewport,
        Math.round(rawViewportWidth),
        Math.round(targetDeviceHeight),
      ].join('::'),
    [template.meta.slug, activeThemeMode, measurementViewport, rawViewportWidth, targetDeviceHeight],
  );

  // Reset the settle/pass-count tracking only when we're genuinely looking at a
  // different template/theme/viewport context -- NOT on every measurement-driven
  // re-split, since `bookInstanceKey` (used by the measurement effect below) changes
  // on every correction pass and would otherwise make "settled" perpetually unreachable.
  useEffect(() => {
    measurementPassCountRef.current = 0;
    hasSettledOnceRef.current = false;
    setIsLayoutSettled(false);

    // Safety net: never leave the page invisible indefinitely (e.g. if a render error
    // or an unexpected zero-category page means the measurement effect never reports
    // settled). Worst case is a brief flash of an unconverged layout instead of a
    // permanently blank page.
    const fallbackTimer = window.setTimeout(() => setIsLayoutSettled(true), 2500);
    return () => window.clearTimeout(fallbackTimer);
  }, [measurementScopeKey]);

  const document = useMemo(
    () => buildPagedFlipDocument(template, data, {
      viewport: measurementViewport,
      pageHeight: targetDeviceHeight,
      measurementScopeKey,
      measurements: measurementSnapshot,
    }),
    [template, data, measurementViewport, measurementScopeKey, measurementSnapshot, targetDeviceHeight],
  );

  const categorySpotlightSlotUsage = useMemo(() => {
    const nodes = getTemplatePagedNodes(template, 'category-spotlight');
    if (nodes.length === 0) {
      return {
        usesSubtitle: true,
        usesPageCategories: false,
      };
    }

    const serializedNodes = JSON.stringify(nodes);
    return {
      usesSubtitle: serializedNodes.includes('page.subtitle'),
      usesPageCategories:
        serializedNodes.includes('page.categories') ||
        serializedNodes.includes('category.') ||
        serializedNodes.includes('item.'),
    };
  }, [template]);

  const visiblePages = useMemo(() => {
    const nonBlankPages = document.pages.filter((page) => {
      if (page.kind === 'blank') return false;
      if (page.kind !== 'category-spotlight') return true;

      const hasRenderedSupport =
        (categorySpotlightSlotUsage.usesSubtitle && pageText(page.subtitle).length > 0) ||
        (categorySpotlightSlotUsage.usesPageCategories && pageHasItems(page)) ||
        (page.promotions || []).length > 0;

      return hasRenderedSupport;
    });
    if (isMobile) {
      return nonBlankPages;
    }
    return ensureEvenPagedFlipPages(nonBlankPages);
  }, [categorySpotlightSlotUsage, document.pages, isMobile]);
  const visibleContentPages = useMemo(
    () => visiblePages.filter((page) => page.kind !== 'blank'),
    [visiblePages],
  );

  const searchParams = useSearchParams();
  const isCoverOnly = searchParams?.get('coverOnly') === '1';

  const typography = useMemo(() => getTypography(templateResolvedVariables), [templateResolvedVariables]);
  const palette = useMemo(
    () => getPalette(template, resolveCanonicalVariables(template, activeThemeMode)),
    [template, activeThemeMode]
  );
  const resolvedCanvasBackground = useMemo(
    () =>
      resolveTemplateTokenString(
        template.canvas?.background?.value || palette.canvasBackground || '#ffffff',
        templateResolvedVariables,
      ) || palette.canvasBackground || '#ffffff',
    [template.canvas?.background?.value, palette.canvasBackground, templateResolvedVariables],
  );
  const ambientCanvasStyle = useMemo(
    () => resolveAmbientCanvasStyle(resolvedCanvasBackground, palette.pageAccent, activeThemeMode),
    [resolvedCanvasBackground, palette.pageAccent, activeThemeMode],
  );
  const bookInstanceKey = useMemo(
    () =>
      [
        template.meta.slug,
        activeThemeMode,
        isMobile ? 'mobile' : 'desktop',
        // Viewport size changes require a remount because the flipbook dimensions
        // change. We use the rounded dimensions rather than the full scope key so
        // minor measurement corrections don't trigger remounts.
        Math.round(rawViewportWidth),
        Math.round(targetDeviceHeight),
        visiblePages.length,
        visiblePages
          .map(
            (page) =>
              [
                page.id,
                page.kind,
                page.layout || 'stacked',
                page.pageNumber,
                // Only include structural category data (IDs + continuation),
                // NOT per-pass metrics that change every measurement cycle.
                (page.categories || [])
                  .map(
                    (category) =>
                      `${category.sourceCategoryId || category.id}:${category.continuation ? 1 : 0}`,
                  )
                  .join(','),
              ].join(':'),
          )
          .join('|'),
      ].join('::'),
    [template.meta.slug, activeThemeMode, isMobile, rawViewportWidth, targetDeviceHeight, visiblePages],
  );

  useEffect(() => {
    if (pageIndex >= visiblePages.length) {
      setPageIndex(0);
    }
  }, [visiblePages.length, pageIndex]);

  useEffect(() => {
    setBookUnavailable(false);
  }, [visiblePages.length, activeThemeMode, isMobile]);

  // Auto-retry: when the flipbook fails, wait briefly then try again (up to
  // MAX_FLIP_RETRIES times). Transient issues like late font loading or a
  // zero-size container on first paint usually succeed on the second attempt.
  useEffect(() => {
    if (!bookUnavailable || flipRetryCount >= MAX_FLIP_RETRIES) return;

    flipRetryTimerRef.current = window.setTimeout(() => {
      flipRetryTimerRef.current = null;
      setFlipRetryCount((c) => c + 1);
      setBookUnavailable(false);
    }, 800 * (flipRetryCount + 1));

    return () => {
      if (flipRetryTimerRef.current != null) {
        window.clearTimeout(flipRetryTimerRef.current);
        flipRetryTimerRef.current = null;
      }
    };
  }, [bookUnavailable, flipRetryCount]);

  useEffect(() => {
    bookRef.current = null;
    setOrientation('portrait');
    setFlipRetryCount(0);
    setBookUnavailable(false);
  }, [bookInstanceKey]);

  // After the book instance changes, hide the flipbook frame briefly while
  // react-pageflip re-initialises. This prevents the flash where all pages
  // appear as a vertical scrollable stack before the library positions them.
  // Skip the first render , the book starts visible so autoSize can measure.
  useEffect(() => {
    if (bookUnavailable) return;
    if (isFirstBookRenderRef.current) {
      isFirstBookRenderRef.current = false;
      return;
    }
    setBookFrameOpacity(0);
    bookFrameOpacityTimerRef.current = window.setTimeout(() => {
      bookFrameOpacityTimerRef.current = null;
      setBookFrameOpacity(1);
    }, 300);
    return () => {
      if (bookFrameOpacityTimerRef.current != null) {
        window.clearTimeout(bookFrameOpacityTimerRef.current);
        bookFrameOpacityTimerRef.current = null;
      }
    };
  }, [bookInstanceKey, bookUnavailable]);

  const commitPageIndex = useCallback((nextPageIndex: number | ((current: number) => number)) => {
    if (typeof window === 'undefined') {
      setPageIndex(nextPageIndex);
      return;
    }

    if (pageIndexFrameRef.current != null) {
      window.cancelAnimationFrame(pageIndexFrameRef.current);
    }

    pageIndexFrameRef.current = window.requestAnimationFrame(() => {
      pageIndexFrameRef.current = null;
      startPageIndexTransition(() => {
        setPageIndex(nextPageIndex);
      });
    });
  }, [startPageIndexTransition]);

  useEffect(() => {
    return () => {
      if (pageIndexFrameRef.current != null) {
        window.cancelAnimationFrame(pageIndexFrameRef.current);
      }
      if (flipRetryTimerRef.current != null) {
        window.clearTimeout(flipRetryTimerRef.current);
      }
      if (bookFrameOpacityTimerRef.current != null) {
        window.clearTimeout(bookFrameOpacityTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;
    let frameOne = 0;
    let frameTwo = 0;
    let resizeFrame = 0;

    const collectMeasurements = () => {
      const measurementRoot = measurementRef.current || rendererRef.current;
      if (cancelled || !measurementRoot) return;

      measurementPassCountRef.current += 1;
      const exceededPassCap = measurementPassCountRef.current > MAX_MEASUREMENT_PASSES;
      if (exceededPassCap) {
        if (process.env.NODE_ENV !== 'production' && !hasSettledOnceRef.current) {
          console.warn(
            `[PagedFlipRenderer] measurement did not converge within ${MAX_MEASUREMENT_PASSES} passes for "${template.meta.slug}" -- forcing settle with the current layout. This usually means a template node is growing/shrinking by a tiny amount each pass; check for content whose size depends on the page split itself.`,
          );
        }
        hasSettledOnceRef.current = true;
        setIsLayoutSettled(true);
        return;
      }

      const nextMeasurements: PagedFlipMeasurementSnapshot = {
        itemWeights: {},
        categoryHeaderWeights: {},
        categoryFooterWeights: {},
        pageBaseWeights: {},
        blockGapWeights: {},
      };
      const sheets = Array.from(
        measurementRoot.querySelectorAll<HTMLElement>('[data-render-role="flipbook-sheet"][data-page-kind]'),
      );

      sheets.forEach((sheet) => {
        const pageKind = sheet.dataset.pageKind as PagedFlipPage['kind'] | undefined;
        const pageLayout = (sheet.dataset.pageLayout as PagedFlipPage['layout']) || 'stacked';

        if (
          pageKind !== 'content' &&
          pageKind !== 'featured-showcase' &&
          pageKind !== 'new-arrivals'
        ) {
          return;
        }

        const bucketKey = buildPagedFlipMeasurementBucketKey(
          measurementScopeKey,
          measurementViewport,
          pageKind,
          pageLayout,
        );
        let measuredBaseHeight = 0;
        const categoryGroups = groupCategoryRoots(sheet);
        const scrollElement = sheet.querySelector<HTMLElement>('[data-render-role="flipbook-page-scroll"]');

        if (scrollElement && categoryGroups.length > 0) {
          const firstCategoryTop = Math.min(
            ...categoryGroups.map((group) => getGroupBounds(group.elements).top),
          );
          const scrollTop = scrollElement.getBoundingClientRect().top;
          const baseHeight = roundMeasuredPixels(firstCategoryTop - scrollTop);
          measuredBaseHeight = baseHeight;
          nextMeasurements.pageBaseWeights[bucketKey] = Math.max(
            nextMeasurements.pageBaseWeights[bucketKey] || 0,
            baseHeight,
          );
        }

        categoryGroups.forEach((group) => {
          const { headerHeight, footerHeight, itemWeights, rows, bottom } = collectCategoryMeasurements(group);
          const categoryKey = buildPagedFlipMeasuredCategoryKey(bucketKey, group.id, group.continuation);
          nextMeasurements.categoryHeaderWeights[categoryKey] = Math.max(
            nextMeasurements.categoryHeaderWeights[categoryKey] || 0,
            headerHeight,
          );
          nextMeasurements.categoryFooterWeights[categoryKey] = Math.max(
            nextMeasurements.categoryFooterWeights[categoryKey] || 0,
            footerHeight,
          );

          itemWeights.forEach((value, itemId) => {
            const itemKey = buildPagedFlipMeasuredItemKey(bucketKey, itemId);
            nextMeasurements.itemWeights[itemKey] = Math.max(
              nextMeasurements.itemWeights[itemKey] || 0,
              value,
            );
          });

          if (scrollElement && bottom > scrollElement.getBoundingClientRect().bottom + 1) {
            nextMeasurements.pageBaseWeights[bucketKey] = Math.max(
              nextMeasurements.pageBaseWeights[bucketKey] || 0,
              measuredBaseHeight,
            );
          }
        });

        if (categoryGroups.length > 1) {
          const sortedGroups = categoryGroups
            .map((group) => ({ group, bounds: getGroupBounds(group.elements) }))
            .sort((left, right) => left.bounds.top - right.bounds.top);

          sortedGroups.forEach((entry, index) => {
            const nextEntry = sortedGroups[index + 1];
            if (!nextEntry) return;
            const gap = roundMeasuredPixels(Math.max(0, nextEntry.bounds.top - entry.bounds.bottom));
            nextMeasurements.blockGapWeights[bucketKey] = Math.max(
              nextMeasurements.blockGapWeights[bucketKey] || 0,
              gap,
            );
          });
        }
      });

      setMeasurementSnapshot((current) => {
        const mergedItems = mergeMeasuredMap(current.itemWeights, nextMeasurements.itemWeights);
        const mergedHeaders = mergeMeasuredMap(
          current.categoryHeaderWeights,
          nextMeasurements.categoryHeaderWeights,
        );
        const mergedFooters = mergeMeasuredMap(
          current.categoryFooterWeights,
          nextMeasurements.categoryFooterWeights,
        );
        const mergedPageBase = mergeMeasuredMap(current.pageBaseWeights, nextMeasurements.pageBaseWeights);
        const mergedBlockGaps = mergeMeasuredMap(current.blockGapWeights, nextMeasurements.blockGapWeights);

        if (
          !mergedItems.changed &&
          !mergedHeaders.changed &&
          !mergedFooters.changed &&
          !mergedPageBase.changed &&
          !mergedBlockGaps.changed
        ) {
          hasSettledOnceRef.current = true;
          setIsLayoutSettled(true);
          return current;
        }

        return {
          itemWeights: mergedItems.next,
          categoryHeaderWeights: mergedHeaders.next,
          categoryFooterWeights: mergedFooters.next,
          pageBaseWeights: mergedPageBase.next,
          blockGapWeights: mergedBlockGaps.next,
        };
      });
    };

    const scheduleCollectMeasurements = () => {
      frameOne = window.requestAnimationFrame(() => {
        frameTwo = window.requestAnimationFrame(collectMeasurements);
      });
    };

    Promise.resolve(window.document.fonts?.ready)
      .catch(() => undefined)
      .then(() => {
        const measurementRoot = measurementRef.current || rendererRef.current;
        return measurementRoot ? waitForImages(measurementRoot) : undefined;
      })
      .finally(() => {
        scheduleCollectMeasurements();
      });

    const resizeObserver = new ResizeObserver(() => {
      if (cancelled) return;
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        if (!cancelled) {
          void waitForImages(measurementRef.current || rendererRef.current || window.document).finally(collectMeasurements);
        }
      });
    });

    if (rendererRef.current) {
      resizeObserver.observe(rendererRef.current);
    }

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameOne);
      window.cancelAnimationFrame(frameTwo);
      window.cancelAnimationFrame(resizeFrame);
      resizeObserver.disconnect();
    };
  }, [bookInstanceKey, measurementScopeKey, measurementViewport]);

  const desktopSpreadFactor = config.desktopSpread === false ? 1 : 2;
  const isDesktopSpreadMode = !isMobile && desktopSpreadFactor > 1;

  const visiblePageIndex = isDesktopSpreadMode
    ? getSpreadStartIndex(pageIndex, Boolean(config.showCoverPage))
    : pageIndex;
  const currentPage = visiblePages[visiblePageIndex];
  const pairedPage = isDesktopSpreadMode && visiblePageIndex > 0 ? visiblePages[visiblePageIndex + 1] : undefined;
  const displayedPages = useMemo(
    () =>
      [currentPage, pairedPage].filter(
        (page): page is PagedFlipPage => page != null && page.kind !== 'blank',
      ),
    [currentPage, pairedPage],
  );
  const displayedContentOrdinals = useMemo(
    () =>
      displayedPages
        .map((page) => visibleContentPages.findIndex((entry) => entry.id === page.id) + 1)
        .filter((ordinal) => ordinal > 0),
    [displayedPages, visibleContentPages],
  );

  const jumpToPageIndex = useCallback(
    (nextPageIndex: number, animated = true) => {
      const boundedPageIndex = Math.min(Math.max(nextPageIndex, 0), Math.max(visiblePages.length - 1, 0));

      const flip = bookRef.current?.pageFlip?.() as any;
      if (!flip || bookUnavailable) {
        commitPageIndex(boundedPageIndex);
        return;
      }

      try {
        if (animated) {
          flip.flip(boundedPageIndex, 'top');
          if (flip.getState() === 'flipping') {
            return;
          }
        }
        flip.turnToPage(boundedPageIndex);
        commitPageIndex(boundedPageIndex);
      } catch {
        setBookUnavailable(true);
        commitPageIndex(boundedPageIndex);
      }
    },
    [bookUnavailable, commitPageIndex, visiblePages.length],
  );

  const jumpToChapter = useCallback(
    (chapterId: string) => {
      const nextPageIndex = visiblePages.findIndex((page) => page.chapterId === chapterId);
      if (nextPageIndex >= 0) {
        jumpToPageIndex(nextPageIndex);
      }
    },
    [visiblePages, jumpToPageIndex],
  );

  const handlePrevious = useCallback(() => {
    const flip = bookRef.current?.pageFlip?.() as any;
    const next = Math.max(pageIndex - 1, 0);

    if (flip && !bookUnavailable) {
      try {
        flip.flipPrev('top');
        if (flip.getState() === 'flipping') {
          return;
        }
        // Fallback to instant turn if flipPrev returned false/did not animate
        flip.turnToPage(next);
      } catch {
        setBookUnavailable(true);
      }
    }

    commitPageIndex(next);
  }, [bookUnavailable, commitPageIndex, pageIndex]);

  const handleNext = useCallback(() => {
    const flip = bookRef.current?.pageFlip?.() as any;
    const total = visiblePages.length;
    const next = Math.min(pageIndex + 1, Math.max(total - 1, 0));

    if (flip && !bookUnavailable) {
      try {
        flip.flipNext('top');
        if (flip.getState() === 'flipping') {
          return;
        }
        // Fallback to instant turn if flipNext returned false/did not animate
        flip.turnToPage(next);
      } catch {
        setBookUnavailable(true);
      }
    }

    commitPageIndex(next);
  }, [bookUnavailable, commitPageIndex, pageIndex, visiblePages.length]);

  const [showSwipeHint, setShowSwipeHint] = useState(false);

  const dismissSwipeHint = useCallback(() => {
    setShowSwipeHint(false);
    try {
      window.localStorage.setItem(SWIPE_HINT_STORAGE_KEY, '1');
    } catch {
      // Storage unavailable , the hint simply shows again next visit.
    }
  }, []);

  useEffect(() => {
    if (guidePreview || visibleContentPages.length <= 1) return;
    try {
      if (window.localStorage.getItem(SWIPE_HINT_STORAGE_KEY)) return;
    } catch {
      // Ignore storage failures and show the hint this visit.
    }
    const showTimer = window.setTimeout(() => setShowSwipeHint(true), 900);
    return () => window.clearTimeout(showTimer);
  }, [guidePreview, visibleContentPages.length]);

  useEffect(() => {
    if (!showSwipeHint) return;
    const hideTimer = window.setTimeout(dismissSwipeHint, 7000);
    return () => window.clearTimeout(hideTimer);
  }, [showSwipeHint, dismissSwipeHint]);

  useEffect(() => {
    if (guidePreview) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (event.key === 'ArrowLeft') {
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [guidePreview, handlePrevious, handleNext]);

  // ─── Window-level touch gesture handler ─────────────────────────────────────
  // We attach directly to window (not to a JSX element) so the listener is
  // guaranteed to fire regardless of whether nested DOM elements stop
  // propagation or the library's internal containers absorb touch events.
  //
  // All mutable values (handleNext, handlePrevious, guidePreview) are accessed
  // via refs so we never need to tear-down/re-register the listeners on
  // dependency changes , the effect runs exactly once on mount.
  // ─────────────────────────────────────────────────────────────────────────────

  const touchStateRef = useRef({
    startX: 0,
    startY: 0,
    startTime: 0,
    tracking: false,
    fired: false,
  });

  // Stable refs so the touch effect never becomes stale
  const handleNextRef = useRef(handleNext);
  const handlePreviousRef = useRef(handlePrevious);
  const dismissSwipeHintRef = useRef(dismissSwipeHint);
  const guidePreviewRef = useRef(guidePreview);
  // Direct access to page state for the swipe handler , bypasses the flip
  // library's animation API which requires useMouseEvents to be active.
  const pageIndexRef = useRef(pageIndex);
  const visiblePagesLengthRef = useRef(visiblePages.length);

  useEffect(() => { handleNextRef.current = handleNext; }, [handleNext]);
  useEffect(() => { handlePreviousRef.current = handlePrevious; }, [handlePrevious]);
  useEffect(() => { dismissSwipeHintRef.current = dismissSwipeHint; }, [dismissSwipeHint]);
  useEffect(() => { guidePreviewRef.current = guidePreview; }, [guidePreview]);
  useEffect(() => { pageIndexRef.current = pageIndex; }, [pageIndex]);
  useEffect(() => { visiblePagesLengthRef.current = visiblePages.length; }, [visiblePages.length]);

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (guidePreviewRef.current || e.touches.length !== 1) return;
      touchStateRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startTime: Date.now(),
        tracking: true,
        fired: false,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      const state = touchStateRef.current;
      if (guidePreviewRef.current || !state.tracking || state.fired || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - state.startX;
      const dy = e.touches[0].clientY - state.startY;
      // Abandon horizontal tracking once the gesture is clearly a vertical scroll.
      // The browser owns vertical panning (touch-action: pan-y); the engine must not
      // claim it. T1 / R29.2 / TPL-02.
      const SCROLL_LOCK_DY = 60;
      if (classify(dx, dy, SCROLL_LOCK_DY) === 'vertical') {
        state.tracking = false;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const state = touchStateRef.current;
      if (guidePreviewRef.current || !state.tracking || state.fired || e.changedTouches.length === 0) {
        return;
      }
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - state.startX;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(endY - state.startY);
      const elapsed = Math.max(Date.now() - state.startTime, 1);
      const velocity = absDx / elapsed;
      const isHorizontal = absDx > absDy;

      const SWIPE_MIN_DX = 40;
      const SWIPE_MAX_TIME = 500;
      const SWIPE_MIN_VELOCITY = 0.2;

      if (isHorizontal && absDx >= SWIPE_MIN_DX && (elapsed < SWIPE_MAX_TIME || velocity >= SWIPE_MIN_VELOCITY)) {
        state.fired = true;
        if (dx > 0) {
          // Swipe right → previous page
          handlePreviousRef.current();
        } else {
          // Swipe left → next page
          handleNextRef.current();
        }
        dismissSwipeHintRef.current();
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  // Intentionally empty deps: runs once on mount, all live values via refs above
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kept as no-ops so JSX props below compile , they're never called since
  // the window listeners above handle everything.
  const onTouchStart = useCallback((_e: React.TouchEvent) => {}, []);
  const onTouchMove = useCallback((_e: React.TouchEvent) => {}, []);
  const onTouchEnd = useCallback((_e: React.TouchEvent) => {}, []);

  const handleFlip = useCallback((event: FlipEvent) => {
    if (typeof event.data === 'number') {
      commitPageIndex(event.data);
    }
    dismissSwipeHint();
  }, [commitPageIndex, dismissSwipeHint]);

  const handleOrientation = useCallback((event: OrientationEvent) => {
    if (event.data === 'portrait' || event.data === 'landscape') {
      setOrientation(event.data);
    }
  }, []);

  const handleManualRetry = useCallback(() => {
    setFlipRetryCount(0);
    setBookUnavailable(false);
  }, []);

  const firstDisplayedOrdinal = displayedContentOrdinals[0] || 1;
  const lastDisplayedOrdinal = displayedContentOrdinals[displayedContentOrdinals.length - 1] || firstDisplayedOrdinal;
  const pageRangeLabel =
    displayedContentOrdinals.length > 1
      ? `Pages ${firstDisplayedOrdinal}-${lastDisplayedOrdinal}`
      : `Page ${firstDisplayedOrdinal}`;
  const remainingPageCount = Math.max(visibleContentPages.length - lastDisplayedOrdinal, 0);
  const firstDisplayedVisibleIndex = displayedPages.length > 0
    ? visiblePages.findIndex((page) => page.id === displayedPages[0].id)
    : visiblePageIndex;
  const lastDisplayedVisibleIndex = displayedPages.length > 0
    ? visiblePages.findIndex((page) => page.id === displayedPages[displayedPages.length - 1].id)
    : visiblePageIndex;
  const hasPreviousContentPage = visiblePages
    .slice(0, Math.max(firstDisplayedVisibleIndex, 0))
    .some((page) => page.kind !== 'blank');
  const hasNextContentPage = visiblePages
    .slice(Math.max(lastDisplayedVisibleIndex, visiblePageIndex) + 1)
    .some((page) => page.kind !== 'blank');
  const showPagerControls = visibleContentPages.length > 1 && !guidePreview;
  const isSingleMobilePage = isMobile && visiblePages.length <= 1;
  
  const rawViewportWidthSync = viewport.width || 360;

  const safeViewportWidth = Math.max(rawViewportWidthSync, 280);

  const horizontalInset = isMobile ? (isSingleMobilePage ? 0 : 4) : 64;

  const availableWidth = Math.max(280, safeViewportWidth - horizontalInset);

  // Maximum caps for "big screens" for clean, clear boundaries
  const MAX_PAGE_WIDTH = 460;

  const singlePageAvailableWidth = isMobile ? availableWidth : Math.floor(availableWidth / desktopSpreadFactor);
  
  const currentBookWidth = Math.min(
    isMobile ? Math.max(260, Math.min(singlePageAvailableWidth, safeViewportWidth - 2)) : singlePageAvailableWidth,
    MAX_PAGE_WIDTH,
  );
  const currentBookHeight = targetDeviceHeight; // Unconditionally perfectly synced with Engine Builder boundaries

  const minBookWidth = isMobile ? currentBookWidth : Math.max(340, Math.round(currentBookWidth * 0.82));
  const maxBookWidth = isMobile ? currentBookWidth : Math.max(currentBookWidth, Math.round(currentBookWidth * 1.08));
  const minBookHeight = isMobile ? currentBookHeight : Math.max(400, Math.round(currentBookHeight * 0.82));
  const maxBookHeight = isMobile ? currentBookHeight : Math.max(currentBookHeight, Math.round(currentBookHeight * 1.08));
  
  const bookFrameWidth = isMobile ? currentBookWidth : currentBookWidth * desktopSpreadFactor;
  const bookStartPage = Math.min(Math.max(pageIndex, 0), Math.max(visiblePages.length - 1, 0));
  const standalonePageOffset = getStandalonePageOffset(
    currentPage,
    pairedPage,
    orientation,
    isMobile,
    Boolean(config.showCoverPage),
    visiblePageIndex,
    visiblePages.length,
    currentBookWidth,
  );

  const shellSurface = `color-mix(in srgb, ${palette.pageBackground} 46%, transparent)`;
  const controlSurface = `color-mix(in srgb, ${palette.pageBackground} 86%, transparent)`;
  const swipeDistance = isMobile ? 30 : 72;

  // Memoize the HTMLFlipBook children so the reference is stable across parent
  // re-renders (viewport changes, measurement updates, etc.). The react-pageflip
  // library's internal useEffect wipes childRef.current when props.children
  // changes reference, causing a race condition where the library can never
  // initialize its stf__parent DOM structure. By depending only on
  // bookInstanceKey (which forces a full remount), children stay stable between
  // the initial mount and library initialization.
  const flipBookChildren = useMemo(
    () =>
      visiblePages.map((page, index) => (
        <FlipBookSheet
          key={page.id}
          template={template}
          data={data}
          document={document}
          page={page}
          pageIndex={index}
          palette={palette}
          typography={typography}
          mode={activeThemeMode}
          variables={templateResolvedVariables}
          config={config}
          isMobile={isMobile}
          pageWidth={currentBookWidth}
          pageHeight={currentBookHeight}
          canvasBackground={resolvedCanvasBackground}
          onJumpToChapter={jumpToChapter}
        />
      )),
    // bookInstanceKey changes force a full remount of HTMLFlipBook, so this
    // memo is effectively "per-book-instance". Including visiblePages.length
    // ensures we don't render stale children after a repagination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookInstanceKey, visiblePages.length],
  );

  if (isCoverOnly && visiblePages.length > 0) {
    const coverPage = visiblePages[0];
    return (
      <div
        className="flex min-h-screen w-full items-center justify-center p-4 bg-transparent"
        style={ambientCanvasStyle}
      >
        <div
          className="relative overflow-hidden shadow-[0_18px_50px_rgba(15,23,32,0.15)] sm:rounded-r-[20px]"
          style={{ width: currentBookWidth, height: currentBookHeight, backgroundColor: palette.pageBackground }}
        >
          <FlipBookSheet
            template={template}
            data={data}
            document={document}
            page={coverPage}
            pageIndex={0}
            palette={palette}
            typography={typography}
            mode={activeThemeMode}
            variables={templateResolvedVariables}
            config={config}
            isMobile={false}
            pageWidth={currentBookWidth}
            pageHeight={currentBookHeight}
            canvasBackground={resolvedCanvasBackground}
            onJumpToChapter={() => {}}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rendererRef}
      className="overflow-x-hidden pb-20 sm:pb-5"
      data-paged-flip-settled={isLayoutSettled ? 'true' : 'false'}
      style={{
        width: '100%',
        maxWidth: '100vw',
        minHeight: viewportOffsetPx ? `calc(100dvh - ${viewportOffsetPx}px)` : '100dvh',
        overflowX: 'hidden',
        // Tell the browser this entire renderer supports vertical panning on mobile.
        // Without this, browsers may incorrectly block vertical scroll when the
        // touch gesture starts inside a nested element.
        touchAction: isMobile ? 'pan-y' : 'auto',
        opacity: isLayoutSettled ? 1 : 0,
        transition: 'opacity 0.25s ease-out',
        ...ambientCanvasStyle,
        colorScheme: activeThemeMode,
        ...TEMPLATE_THEME_SURFACE_VARS[activeThemeMode],
      }}
    >
      <div
        ref={measurementRef}
        aria-hidden="true"
        data-render-role="flipbook-measurement-deck"
        className="pointer-events-none fixed left-[-10000px] top-0 z-[-1] overflow-hidden opacity-0"
        style={{
          width: currentBookWidth,
          // height:0 ensures this deck never contributes to scroll height
          // even if CSS positioning is overridden by a parent or print styles.
          height: 0,
          visibility: 'hidden',
        }}
      >
        {visiblePages.map((page, index) => (
          <div
            key={`measure-${page.id}`}
            style={{
              width: currentBookWidth,
              height: currentBookHeight,
              overflow: 'hidden',
            }}
          >
            <FlipBookSheet
              template={template}
              data={data}
              document={document}
              page={page}
              pageIndex={index}
              palette={palette}
              typography={typography}
              mode={activeThemeMode}
              variables={templateResolvedVariables}
              config={config}
              isMobile={isMobile}
              pageWidth={currentBookWidth}
              pageHeight={currentBookHeight}
              canvasBackground={resolvedCanvasBackground}
              onJumpToChapter={() => {}}
            />
          </div>
        ))}
      </div>
      <div
        className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-0 py-0 sm:px-4 sm:py-5 lg:px-6"
        style={{ minHeight: viewportOffsetPx ? `calc(100dvh - ${viewportOffsetPx}px)` : '100dvh' }}
      >
        <div className="flex-1 space-y-3">
          <div
            className="relative border-x-0 px-0 py-0 shadow-[0_18px_50px_rgba(15,23,32,0.10)] sm:rounded-3xl sm:border sm:px-4 sm:py-3 transition-colors duration-300"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{
              overflow: 'hidden',
              // pan-y (not none) frees native vertical scroll on the page itself
              // while leaving horizontal swipe to the flip engine (T1 / R29.2).
              touchAction: isMobile ? 'pan-y' : 'auto',
              backgroundColor: shellSurface,
              borderColor: palette.border,
              maxWidth: '100vw',
            }}
          >
            <BookBindingOverlay
              isMobile={isMobile}
              orientation={orientation}
              palette={palette}
              mode={activeThemeMode}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-8 top-0 z-[5] h-28 rounded-b-[28px]"
              style={{
                background: `linear-gradient(180deg, color-mix(in srgb, ${palette.pageAccentSoft} 26%, transparent), transparent)`,
              }}
            />
            <div
              ref={bookShellRef}
              className="relative z-[10] flex w-full items-center justify-center"
              data-render-role="flipbook-book-shell"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              style={{
                minHeight: currentBookHeight,
                overflow: 'hidden',
                touchAction: isMobile ? 'pan-y' : 'auto',
              }}
            >
              <div
                className="flex justify-center w-full"
                data-render-role="flipbook-book-frame"
                style={{
                  maxWidth: '100%',
                  transform: `translateX(${standalonePageOffset})`,
                  transition: 'transform 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  willChange: 'transform',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <div
                  data-render-role="flipbook-book-inner"
                  style={{
                    opacity: bookFrameOpacity,
                    transition: 'opacity 0.2s ease-out',
                    width: bookFrameWidth,
                    height: currentBookHeight,
                    maxHeight: currentBookHeight,
                  }}
                >
                {!bookUnavailable ? (
                  <HTMLFlipBook
                    key={bookInstanceKey}
                    ref={bookRef}
                    width={currentBookWidth}
                    height={currentBookHeight}
                    size="stretch"
                    minWidth={minBookWidth}
                    maxWidth={maxBookWidth}
                    minHeight={minBookHeight}
                    maxHeight={maxBookHeight}
                    drawShadow
                    flippingTime={900}
                    usePortrait
                    startPage={bookStartPage}
                    startZIndex={5}
                    autoSize
                    maxShadowOpacity={0.2}
                    showCover={config.showCoverPage}
                    mobileScrollSupport={false}
                    swipeDistance={swipeDistance}
                    clickEventForward
                    useMouseEvents={!isMobile}
                    disableFlipByClick={!isMobile}
                    renderOnlyPageLengthChange={true}
                    className="mx-auto"
                    style={{
                      margin: '0 auto',
                      padding: '0',
                      position: 'relative',
                      width: '100%',
                      maxWidth: `${bookFrameWidth}px`,
                      touchAction: isMobile ? 'pan-y' : 'auto',
                    }}
                    onFlip={handleFlip}
                    onChangeOrientation={handleOrientation}
                  >
                  {flipBookChildren}
                </HTMLFlipBook>
              ) : (
                <div className="flex min-h-[320px] items-center justify-center px-6 py-10 text-center">
                  <div
                    className="max-w-md rounded-2xl border px-6 py-8"
                    style={{
                      backgroundColor: controlSurface,
                      borderColor: palette.border,
                      color: palette.itemTitle,
                      fontFamily: typography.body,
                    }}
                  >
                    {flipRetryCount < MAX_FLIP_RETRIES ? (
                      <>
                        <div
                          className="mx-auto mb-3 h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"
                          style={{ borderColor: `${palette.pageAccent}40`, borderTopColor: palette.pageAccent }}
                        />
                        <p className="text-sm" style={{ color: palette.eyebrow }}>
                          Reloading flipbook…
                        </p>
                      </>
                    ) : (
                      <>
                        <h2 className="text-lg font-semibold" style={{ fontFamily: typography.heading }}>
                          Flipbook unavailable
                        </h2>
                        <p className="mt-2 text-sm" style={{ color: palette.eyebrow }}>
                          The interactive menu could not load.
                        </p>
                        <button
                          type="button"
                          onClick={handleManualRetry}
                          className="mt-4 rounded-full border px-5 py-2 text-sm font-semibold transition hover:scale-105"
                          style={{
                            backgroundColor: palette.pageAccent,
                            borderColor: palette.pageAccent,
                            color: '#fff',
                            fontFamily: typography.body,
                          }}
                        >
                          Try again
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
              </div>
              </div>
            </div>

            {showPagerControls ? (
              <>
                {/* R29.3 — edge tap zones. A guest who cannot discover the swipe
                    is never stuck. Vertical drags still scroll natively
                    (touch-action: pan-y); only a tap or a horizontal swipe turns
                    the page. Disabled at the first/last page so they never block
                    scroll or taps there. */}
                <button
                  type="button"
                  aria-hidden
                  tabIndex={-1}
                  onClick={handlePrevious}
                  disabled={bookUnavailable || !hasPreviousContentPage}
                  className="absolute left-0 top-0 z-[18] h-full w-8 bg-transparent disabled:pointer-events-none sm:w-10"
                  style={{ touchAction: 'pan-y' }}
                />
                <button
                  type="button"
                  aria-hidden
                  tabIndex={-1}
                  onClick={handleNext}
                  disabled={bookUnavailable || !hasNextContentPage}
                  className="absolute right-0 top-0 z-[18] h-full w-8 bg-transparent disabled:pointer-events-none sm:w-10"
                  style={{ touchAction: 'pan-y' }}
                />
                <button
                  type="button"
                  data-render-role="flipbook-prev"
                  onClick={handlePrevious}
                  disabled={bookUnavailable || !hasPreviousContentPage}
                  aria-label="Previous page"
                  className="absolute left-1 top-1/2 z-[20] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border shadow-[0_10px_30px_rgba(15,23,32,0.16)] backdrop-blur-md transition hover:scale-105 disabled:pointer-events-none disabled:opacity-35 sm:left-2"
                  style={{ backgroundColor: controlSurface, borderColor: palette.border, color: palette.itemTitle }}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  data-render-role="flipbook-next"
                  onClick={handleNext}
                  disabled={bookUnavailable || !hasNextContentPage}
                  aria-label="Next page"
                  className="absolute right-1 top-1/2 z-[20] flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border shadow-[0_10px_30px_rgba(15,23,32,0.16)] backdrop-blur-md transition hover:scale-105 disabled:pointer-events-none disabled:opacity-35 sm:right-2"
                  style={{ backgroundColor: controlSurface, borderColor: palette.border, color: palette.itemTitle }}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            ) : null}
          </div>

          {showSwipeHint && showPagerControls ? (
            <div
              className={`pointer-events-none fixed inset-x-0 flex justify-center px-4 max-sm:bottom-36 ${bottomChromeOffsetPx ? 'sm:bottom-[88px]' : 'bottom-16 sm:bottom-20'} z-[105]`}
            >
              <style>{'@keyframes mnrq-swipe-nudge { 0%, 100% { transform: translateX(0); opacity: 0.9; } 50% { transform: translateX(-7px); opacity: 0.5; } }'}</style>
              <div
                className="flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-[0_12px_34px_rgba(15,23,32,0.18)] backdrop-blur-md"
                style={{ backgroundColor: controlSurface, borderColor: palette.border, color: palette.itemTitle, fontFamily: typography.body }}
              >
                <ChevronLeft className="h-4 w-4" style={{ animation: 'mnrq-swipe-nudge 1.4s ease-in-out infinite' }} />
                {isMobile ? 'Swipe left or right to turn the page' : 'Drag or swipe to turn the page'}
              </div>
            </div>
          ) : null}

          {showPagerControls ? (
            <div
              className={`pointer-events-none fixed left-1/2 z-[100] -translate-x-1/2 max-sm:bottom-36 ${bottomChromeOffsetPx ? 'sm:bottom-[72px]' : 'bottom-3 sm:bottom-5'}`}
            >
              <div
                className="flex items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs shadow-[0_12px_34px_rgba(15,23,32,0.12)] backdrop-blur-md sm:text-sm"
                style={{ backgroundColor: controlSurface, borderColor: palette.border, color: palette.itemTitle, fontFamily: typography.body }}
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: palette.pageAccent }} />
                <span className="font-semibold">
                  {pageRangeLabel} <span className="font-normal opacity-70">of {visibleContentPages.length}</span>
                </span>
                <span aria-hidden className="opacity-50">·</span>
                <span className="font-medium opacity-75">{remainingPageCount === 0 ? 'Last page' : 'Swipe to turn'}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default PagedFlipRenderer;
